import { useEffect, useMemo, useState } from "react";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";
import { environment } from "environment/environment";

const EMPTY_SNAPSHOT = Object.freeze({
  connectedBrokerCount: 0,
  totalOrders: 0,
  brokers: [],
  refreshedAt: null,
});

const useBrokerOrderWebsocket = (
  accessToken,
  socketUrl = environment.stockApiDayRangeDataWSUrl,
  destination = environment.brokerOrderDestination
) => {
  const [snapshot, setSnapshot] = useState(EMPTY_SNAPSHOT);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState("");

  useEffect(() => {
    if (typeof window === "undefined" || !accessToken) {
      setIsConnected(false);
      return undefined;
    }

    const stompClient = new Client({
      webSocketFactory: () => new SockJS(socketUrl),
      connectHeaders: { Authorization: `Bearer ${accessToken}` },
      reconnectDelay: 5000,
      connectionTimeout: 10000,
      heartbeatIncoming: 10000,
      heartbeatOutgoing: 10000,
      discardWebsocketOnCommFailure: true,
      debug: () => {},
    });

    stompClient.onConnect = () => {
      setIsConnected(true);
      setConnectionError("");
      stompClient.subscribe(destination, (message) => {
        try {
          const nextSnapshot = JSON.parse(message.body);
          setSnapshot({
            ...EMPTY_SNAPSHOT,
            ...nextSnapshot,
            brokers: Array.isArray(nextSnapshot?.brokers)
              ? nextSnapshot.brokers
              : [],
          });
        } catch (error) {
          console.error("Unable to read broker order update:", error);
          setConnectionError("The latest broker order update could not be read.");
        }
      });
    };

    stompClient.onStompError = (frame) => {
      setIsConnected(false);
      setConnectionError(
        frame.headers?.message || "Broker order connection was rejected."
      );
    };

    stompClient.onWebSocketError = () => {
      setIsConnected(false);
      setConnectionError("Unable to connect to live broker orders.");
    };

    stompClient.onWebSocketClose = () => setIsConnected(false);
    stompClient.onDisconnect = () => setIsConnected(false);
    stompClient.activate();

    return () => {
      setIsConnected(false);
      if (stompClient.active) {
        void stompClient.deactivate().catch((error) => {
          console.error("Unable to close broker order connection:", error);
        });
      }
    };
  }, [accessToken, destination, socketUrl]);

  const orders = useMemo(
    () =>
      snapshot.brokers.flatMap((broker) =>
        (Array.isArray(broker.orders) ? broker.orders : []).map((order) => ({
          ...order,
          brokerAccountId: order.brokerAccountId || broker.brokerAccountId,
          brokerAccountName:
            order.brokerAccountName || broker.brokerAccountName,
          brokerType: order.brokerType || broker.brokerType,
        }))
      ),
    [snapshot]
  );

  return { snapshot, orders, isConnected, connectionError };
};

export default useBrokerOrderWebsocket;
